/*
  The way back for an element the SME rejected, and for one the engineer withdrew (workflow 16, transition T9; CP-07).
  Two small dialogs, used by the element page and by the lane "Back from the SME": nothing else sent `repropose` or
  `propose_again` before, so a rejected element could only be withdrawn.

  - A rejected element is proposed again with new reasons (`repropose`): the reasons go to the SME with the element.
  - A withdrawn element is proposed again as a card in one of the engineer's active sessions (`propose_again`), so the
    engineer settles it like any other card.

  One element at a time, in both (DEC-1016-29).
*/

import { useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { Link } from "react-router-dom";

import { refusalText } from "../api/errors";
import { useApiCommand, useApiQuery } from "../api/hooks";
import type { QueryName } from "../api/routes";
import type { Id } from "../api/types";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { showToast } from "../ui/toastStore";

/** What a proposal again changes on the screens that show the element. */
const STALE: readonly QueryName[] = ["element", "item_page", "back_lane", "inbox", "sme_card", "session_cards", "session", "list_elements", "family_tree", "item_snapshot", "home"];

function useInvalidateAfter() {
  const client = useQueryClient();
  return () => {
    for (const name of STALE) void client.invalidateQueries({ queryKey: [name] });
  };
}

export interface ProposeAgainTarget {
  id: Id;
  name: string;
  version: number;
}

/** Give the SME new reasons for a rejected element, then send it back to them. */
export function ReproposeDialog({ element, onClose }: { element: ProposeAgainTarget; onClose: () => void }) {
  const fieldId = useId();
  const [reasons, setReasons] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const repropose = useApiCommand("repropose");
  const invalidate = useInvalidateAfter();

  const submit = () => {
    if (!reasons.trim()) {
      setProblem("Say why the SME should look at it again.");
      return;
    }
    setProblem(null);
    repropose.mutate(
      { id: element.id, expected_version: element.version, reasons: reasons.trim() },
      {
        onSuccess: () => {
          invalidate();
          showToast({ text: `${element.name} is proposed again. It waits for the SME.` });
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open
      closeOnBackdrop={false}
      onClose={repropose.isPending ? () => undefined : onClose}
      title={`Propose ${element.name} again`}
      footer={
        <>
          <Button onClick={onClose} disabled={repropose.isPending}>
            Cancel
          </Button>
          <Button variant="primary" busy={repropose.isPending} onClick={submit}>
            Propose it again
          </Button>
        </>
      }
    >
      <div className="stack">
        <p>The SME rejected it. Give the reasons they should look at it again. It then waits for the SME once more; nothing else about it changes.</p>
        <label className="field" htmlFor={fieldId}>
          <span className="field__label">
            New reasons <span className="field__required">(needed)</span>
          </span>
          <textarea id={fieldId} className="field__input" rows={3} value={reasons} onChange={(event) => setReasons(event.target.value)} aria-invalid={problem ? true : undefined} data-autofocus />
          {problem && (
            <span role="alert" className="field__error">
              {problem}
            </span>
          )}
        </label>
        {repropose.error && (
          <p role="alert" className="form-error">
            {refusalText(repropose.error)}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/** Bring a withdrawn element back as a card in one of the engineer's active sessions. */
export function ProposeAgainDialog({ element, onClose }: { element: { id: Id; name: string }; onClose: () => void }) {
  const mine = useApiQuery("list_sessions", { mine: true });
  const active = (mine.data ?? []).filter((session) => session.status === "active");
  const [chosen, setChosen] = useState("");
  const proposeAgain = useApiCommand("propose_again");
  const invalidate = useInvalidateAfter();
  // One session is chosen for the person; with several, they pick.
  const sessionId = chosen || (active.length === 1 ? (active[0]?.id ?? "") : "");

  const submit = () => {
    if (!sessionId) return;
    proposeAgain.mutate(
      { id: element.id, session_id: sessionId },
      {
        onSuccess: () => {
          invalidate();
          showToast({ text: `${element.name} is proposed again. Its card is in the tray of that session.`, action: { label: "Open the session", to: `/sessions/${encodeURIComponent(sessionId)}` } });
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open
      closeOnBackdrop={false}
      onClose={proposeAgain.isPending ? () => undefined : onClose}
      title={`Propose ${element.name} again`}
      footer={
        <>
          <Button onClick={onClose} disabled={proposeAgain.isPending}>
            Cancel
          </Button>
          <Button variant="primary" busy={proposeAgain.isPending} disabled={!sessionId} onClick={submit}>
            Propose it again
          </Button>
        </>
      }
    >
      <div className="stack">
        <p>It comes back as a card in one of your sessions. You accept, change or decline it there, like any other card.</p>
        {mine.isPending && <p className="muted">Reading your sessions</p>}
        {mine.isError && (
          <p role="alert" className="form-error">
            {refusalText(mine.error)}
          </p>
        )}
        {mine.data && active.length === 0 && (
          <p>
            You have no session that is open. <Link to="/sessions/new">Start a session</Link>, then come back to propose it again.
          </p>
        )}
        {active.length > 0 && (
          <fieldset className="field field--group">
            <legend className="field__label">In which session?</legend>
            {active.map((session) => (
              <label key={session.id} className="field__checkline">
                <input type="radio" name="propose-again-session" checked={sessionId === session.id} onChange={() => setChosen(session.id)} />
                {session.item?.name ?? session.new_item_name ?? "No single item"}
                <span className="muted">: {session.goal}</span>
              </label>
            ))}
          </fieldset>
        )}
        {proposeAgain.error && (
          <p role="alert" className="form-error">
            {refusalText(proposeAgain.error)}
          </p>
        )}
      </div>
    </Dialog>
  );
}
